
import { Component, mixins, Prop } from 'nuxt-property-decorator';
import { IGameItemForSaleByGala } from '~/store/games/types';
import ProductSupply from '~/mixins/ProductSupply';
import {
  ISanityDocuments,
  SanityDocumentSchema,
  ProjectPageVariation,
} from '~/types/sanity-documents';
import Badge from '~/components/UIComponents/Badge.vue';
import BurnCountdownBadge from './BurnCountdownBadge.vue';
import NftCardBurnInfo from './NftCardBurnInfo.vue';
import NftCardLayout from './NftCardLayout.vue';
import NftCardMedia from './NftCardMedia.vue';
import NftCardReward from './NftCardReward.vue';
import NftCardSkeletonLoader from './NftCardSkeletonLoader.vue';
import PriceChangeCountdown from './PriceChangeCountdown.vue';
import SaleItemPrice from './SaleItemPrice.vue';
import SupplyRemainingBadge from './SupplyRemainingBadge.vue';

@Component({
  components: {
    Badge,
    BurnCountdownBadge,
    NftCardBurnInfo,
    NftCardLayout,
    NftCardMedia,
    NftCardReward,
    NftCardSkeletonLoader,
    PriceChangeCountdown,
    SaleItemPrice,
    SupplyRemainingBadge,
  },
})
export default class NftCard extends mixins(ProductSupply) {
  @Prop(Object) readonly item!: IGameItemForSaleByGala;
  @Prop(String) readonly slug!: string;
  @Prop(String) readonly colLayoutBp!: 'sm' | 'md' | 'lg';

  shouldShowMore = false;
  schemas = SanityDocumentSchema;
  variations = ProjectPageVariation;

  async fetch() {
    if (this.item?.filmNftSlug || this.slug) {
      this.$sanityLookup.fetchDocument({
        slug: this.item?.filmNftSlug || this.slug,
        _type: this.schemas.NFT_CARD,
        fetchReferences: true,
      });
    }
  }

  get variation() {
    return this.$sanityLookup.getSiteConfig()?.data?.projectPageVariation;
  }

  get shouldUse2ColLayout() {
    return this.variation === this.variations.V2;
  }

  get isLoading() {
    return this.docState?.isLoading || this.slugState.isLoading;
  }

  get docState() {
    return (
      this.$sanityLookup.getDocumentFromSlug<
        ISanityDocuments[SanityDocumentSchema.NFT_CARD]
      >({
        slug: this.item?.filmNftSlug || this.slug,
        _type: this.schemas.NFT_CARD,
      }) || {
        isLoading: false,
        hasError: false,
      }
    );
  }

  get slugState() {
    return (
      this.$sanityLookup.getSlug({
        _type: this.schemas.NFT_CARD,
        slug: this.slug,
      }) || {
        isLoading: false,
        hasError: false,
      }
    );
  }

  get projectDoc() {
    const refId = this.docState?.data?.documentReference?._ref;
    return this.$sanityLookup.getDocumentFromReference<
      ISanityDocuments[SanityDocumentSchema.PROJECT]
    >({
      _ref: refId,
    })?.data;
  }

  get refState() {
    const refId = this.docState?.data?.documentReference?._ref;
    return (
      this.$sanityLookup.getReference({
        _ref: refId,
      }) || {
        isLoading: false,
        hasError: false,
      }
    );
  }

  get description() {
    return this.docState?.data?.description || [];
  }

  get discountName() {
    return (
      this.docState?.data?.discountName ||
      this.$t('components.films.filmItemForSale.discountPrice')
    );
  }

  get rewards() {
    return this.docState?.data?.rewards || [];
  }

  get defaultCurrency() {
    const defaultSelectedCurrency =
      this.item?.prices.filter((item) => item.symbol === 'CC-STRIPE').length > 0
        ? 'CC-STRIPE'
        : this.item?.prices?.[this.item.prices.length - 1]?.symbol;
    return defaultSelectedCurrency;
  }

  get shouldShowRewards() {
    return this.rewards?.length && this.docState?.data?.shouldShowRewards;
  }

  get shouldShowSupply() {
    if (!this.docState?.data?.shouldShowSupplyLimit) {
      return false;
    }
    return (!!this.item?.tiers && this.supplyInTier) || this.supplyLimit;
  }

  get completelySoldOutText() {
    return this.$t('components.store.checkout.soldOut');
  }

  get rarityRef() {
    return this.docState?.data?.rarity?._ref;
  }

  get rarityLabel() {
    return this.rarityData?.label;
  }

  get rarityColor() {
    return this.rarityData?.hexcode || '#999';
  }

  get subtitle() {
    return this.projectDoc?.title;
  }

  get tagRef() {
    return this.docState?.data?.badge?._ref;
  }

  get title() {
    return (
      this.docState?.data?.title || this.item?.displayName || this.item?.name
    );
  }

  get linkToStore() {
    if (!this.item?.baseId) {
      return '';
    }

    const query = this.defaultCurrency
      ? `?currency=${this.defaultCurrency}`
      : ``;
    return `/collections/buy-item/${this.item.baseId}${query}`;
  }

  get discount() {
    if (this.price?.basePrice && this.price?.price) {
      const discountAmount = 1 - +this.price.price / +this.price.basePrice;
      return Math.max(discountAmount, 0);
    }
    return 0;
  }

  get price() {
    if (!this.item?.prices?.length) {
      return undefined;
    }
    return this.item.prices[0];
  }
}
